// Library methods
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

// MUI Components
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import SearchBox from "../../../components/UI/SearchBox";

// Utilities
import useClinic from "../../../hooks/useClinic";
import { allLanguageMap } from "../../../i18n/languages";
import { languageMap } from "../../../i18n/languages";
import { getComparator } from "../../../utils/tableHelper";

const LanguageSettings = ({ currentSettings, handleUpdateSettings }) => {
  // internationalization
  const { t } = useTranslation();

  // clinic context
  const { clinicId } = useClinic();

  const clinicLanguage = useMemo(
    () => currentSettings?.clinicDefaultLanguage,
    [currentSettings?.clinicDefaultLanguage]
  );
  const patientLanguage = useMemo(
    () => currentSettings?.patientDefaultLanguage,
    [currentSettings?.patientDefaultLanguage]
  );

  const sortedLanguageList = useMemo(() => {
    const priorityKeys = ["en", "fr"];
    const priorityLanguages = [];
    const otherLanguages = [];

    Object.entries(allLanguageMap).forEach(([key, value]) => {
      const formattedValue = { ...value };
      if (priorityKeys.includes(key)) {
        formattedValue.name = t(value.name);
        priorityLanguages.push(formattedValue);
      } else {
        formattedValue.name = t(value.name);
        otherLanguages.push(formattedValue);
      }
    });
    const res = [
      ...priorityLanguages,
      ...otherLanguages.sort(getComparator("asc", ["name"])),
    ];
    return res;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, clinicId]);

  const availableClinicLanguages = useMemo(
    () => Object.keys(languageMap).map((key) => languageMap[key]),
    []
  );

  const handleSetClinicLang = useCallback(
    (value) => {
      // only update the clinic language if:
      // the value is different than the current setting
      // the value is a valid type that exists in availableClinicLanguages
      if (
        value?.val &&
        value?.val?.toLowerCase() !== clinicLanguage?.val?.toLowerCase() &&
        availableClinicLanguages.find(
          (lang) => lang?.val?.toLowerCase() === value?.val?.toLowerCase()
        )
      ) {
        handleUpdateSettings({
          ...currentSettings,
          clinicDefaultLanguage: value,
        });
      }
    },
    [
      clinicLanguage,
      availableClinicLanguages,
      currentSettings,
      handleUpdateSettings,
    ]
  );

  const handleSetPatientLang = useCallback(
    (value) => {
      // only update the patient language if:
      // the value is different than the current setting
      // the value is a valid type that exists in sortedLanguageList
      if (
        value?.val &&
        value?.val?.toLowerCase() !== patientLanguage?.val?.toLowerCase() &&
        sortedLanguageList.find(
          (lang) => lang?.val?.toLowerCase() === value?.val?.toLowerCase()
        )
      ) {
        handleUpdateSettings({
          ...currentSettings,
          patientDefaultLanguage: value,
        });
      }
    },
    [patientLanguage, sortedLanguageList, currentSettings, handleUpdateSettings]
  );

  return (
    <Box display="flex" flexDirection="column" gap="20px">
      <Box display="flex" flexDirection="column" gap="8px">
        <Typography fontSize={{ xs: "18px", sm: "20px" }} fontWeight={600}>
          {t("settings_clinic_default_language")}
        </Typography>
        <Typography fontSize="14px" mb="12px" color="#5B6D7A">
          {t("language_site_description")}
        </Typography>
        <SearchBox
          data={availableClinicLanguages}
          state={clinicLanguage}
          setState={handleSetClinicLang}
          label={t("patients_modal_language")}
        />
      </Box>
      <Box display="flex" flexDirection="column" gap="8px">
        <Typography fontSize={{ xs: "18px", sm: "20px" }} fontWeight={600}>
          {t("settings_patient_default_language")}
        </Typography>
        <Typography fontSize="14px" mb="12px" color="#5B6D7A">
          {t("language_patient_description")}
        </Typography>
        <SearchBox
          data={sortedLanguageList}
          state={patientLanguage}
          setState={handleSetPatientLang}
          label={t("patients_modal_language")}
        />
      </Box>
    </Box>
  );
};

export default LanguageSettings;
